import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Copy, Trash2, RefreshCw, Bug } from "lucide-react";
import { toast } from "sonner";
import {
  clearErrorLog,
  collectEnvironment,
  formatReport,
  readErrorLog,
  type ErrorEntry,
} from "@/lib/error-log";

export const Route = createFileRoute("/diagnostico")({
  head: () => ({
    meta: [
      { title: "Diagnóstico técnico — FutBux" },
      {
        name: "description",
        content:
          "Relatório técnico do navegador com erros registrados, para diagnóstico de problemas no FutBux.",
      },
      { property: "og:title", content: "Diagnóstico técnico — FutBux" },
      {
        property: "og:description",
        content: "Relatório técnico do navegador com erros registrados no FutBux.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: DiagnosticoPage,
});

function DiagnosticoPage() {
  const [entries, setEntries] = useState<ErrorEntry[]>([]);
  const [env, setEnv] = useState<Record<string, unknown>>({});

  const refresh = () => {
    setEntries(readErrorLog());
    setEnv(collectEnvironment());
  };

  useEffect(() => {
    refresh();
  }, []);

  const copy = async () => {
    const text = formatReport();
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Relatório copiado. Cole aqui no chat.");
    } catch {
      toast.error("Não foi possível copiar. Selecione o texto abaixo manualmente.");
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4" translate="no">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <Bug className="h-6 w-6 text-primary" /> Diagnóstico técnico
        </h1>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={refresh}>
            <RefreshCw className="mr-2 h-4 w-4" /> Atualizar
          </Button>
          <Button size="sm" onClick={copy}>
            <Copy className="mr-2 h-4 w-4" /> Copiar relatório
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              clearErrorLog();
              refresh();
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" /> Limpar
          </Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        Abra esta página no navegador onde o problema acontece, depois de reproduzir o erro.
        Clique em <strong>Copiar relatório</strong> e cole no chat de suporte.
      </p>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Ambiente</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-1 text-xs sm:grid-cols-2">
          {Object.entries(env).map(([k, v]) => (
            <div key={k} className="flex gap-2 break-all">
              <span className="text-muted-foreground">{k}:</span>
              <span className="font-medium">{String(v)}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Erros registrados ({entries.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {entries.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nenhum erro registrado neste navegador ainda.
            </p>
          ) : (
            entries.map((e, i) => (
              <div key={`${e.at}-${i}`} className="space-y-1 rounded border p-3">
                <div className="text-xs text-muted-foreground">
                  {new Date(e.at).toLocaleString("pt-BR")} · {e.kind} · {e.route}
                </div>
                <div className="text-sm font-semibold text-destructive">
                  {e.name}: {e.message}
                </div>
                {e.breadcrumbs.length > 0 && (
                  <div className="text-[11px] text-muted-foreground">
                    Últimas ações: {e.breadcrumbs.join(" → ")}
                  </div>
                )}
                {e.componentStack && (
                  <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 text-[11px]">
                    {e.componentStack.trim()}
                  </pre>
                )}
                {e.stack && (
                  <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 text-[11px]">
                    {e.stack.trim()}
                  </pre>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Texto do relatório</CardTitle>
        </CardHeader>
        <CardContent>
          <textarea
            readOnly
            value={formatReport()}
            className="h-64 w-full rounded border bg-muted p-2 font-mono text-[11px]"
          />
        </CardContent>
      </Card>
    </div>
  );
}
